import type { Option } from "@tento-lona/sheets";
import { DateTime, NaiveDate, TimezoneRegion, Utc } from "@tento-chrono";
import { nonnull } from "@tento-core/assert";

import { FlightInfo } from "./flight-info.ts";
import { ResolvedIndex } from "./resolved-index.ts";

type Segment<Info> =
  | {
      type: "departure";
      flight: FlightInfo<Info>;
      date: NaiveDate;
      tpUtc: DateTime<Utc>;
      tz: TimezoneRegion;
    }
  | {
      type: "arrival";
      flight: FlightInfo<Info>;
      date: NaiveDate;
      tpUtc: DateTime<Utc>;
      tz: TimezoneRegion;
    }
  | {
      type: "in-flight";
      flight: FlightInfo<Info>;
    }
  | {
      type: "collapsed";
      date: NaiveDate;
      tz: TimezoneRegion;
      arrivalTpUtc: DateTime<Utc>;
      departureTpUtc: DateTime<Utc>;
      arrivalFlight: FlightInfo<Info>;
      departureFlight: FlightInfo<Info>;
    };

/** `xs[i]`, or a `RangeError` naming `what` when there is none. */
function at<T>(xs: readonly T[], i: number, what: string): T {
  const value = xs[i];
  if (value === undefined) {
    throw new RangeError(`FlightMapper: ${what} ${i} out of range (length ${xs.length})`);
  }
  return value;
}

function getSegmentDate<Info>(seg: Segment<Info>): Option<NaiveDate> {
  switch (seg.type) {
    case "departure":
    case "arrival":
    case "collapsed":
      return seg.date;
    case "in-flight":
      return null;
  }
}

function getSegmentFlight<Info>(
  seg: Segment<Info>,
): Option<FlightInfo<Info>> {
  switch (seg.type) {
    case "departure":
    case "arrival":
    case "in-flight":
      return seg.flight;
    case "collapsed":
      return seg.departureFlight; // Use departure flight as primary
  }
}

function getSegmentTz<Info>(seg: Segment<Info>): Option<TimezoneRegion> {
  switch (seg.type) {
    case "departure":
    case "arrival":
    case "collapsed":
      return seg.tz;
    case "in-flight":
      return null;
  }
}

/**
 * Maps a date anchor's timeline across a list of flights, so each column
 * knows the timezone the traveller is in.
 */
export class FlightMapper<Info> {
  readonly #segments: Segment<Info>[];
  readonly #idxs: number[];
  #lastPos: number;

  constructor(
    readonly anchor: NaiveDate,
    readonly flights: FlightInfo<Info>[],
    readonly initialTz: TimezoneRegion,
  ) {
    const segments: Segment<Info>[] = [];

    for (const [i, f] of this.flights.entries()) {
      const prevFlight = i > 0 ? at(this.flights, i - 1, "flight") : null;

      // Check if current departure should collapse with previous arrival
      const shouldCollapse =
        prevFlight &&
        segments.at(-1)?.type === "arrival" &&
        prevFlight.end.ndt.date.equals(f.start.ndt.date) &&
        prevFlight.end.tz.fullname === f.start.tz.fullname;

      if (shouldCollapse) {
        // Replace the last arrival segment with a collapsed segment
        segments[segments.length - 1] = {
          type: "collapsed",
          date: f.start.ndt.date,
          tz: f.start.tz,
          arrivalTpUtc: prevFlight.end.tz
            .toWallClock(prevFlight.end.ndt)
            .toUtc(),
          departureTpUtc: f.start.tz.toWallClock(f.start.ndt).toUtc(),
          arrivalFlight: prevFlight,
          departureFlight: f,
        };
      } else {
        segments.push({
          type: "departure",
          flight: f,
          date: f.start.ndt.date,
          tpUtc: f.start.tz.toWallClock(f.start.ndt).toUtc(),
          tz: f.start.tz,
        });
      }

      segments.push({ type: "in-flight", flight: f });
      segments.push({
        type: "arrival",
        flight: f,
        date: f.end.ndt.date,
        tpUtc: f.end.tz.toWallClock(f.end.ndt).toUtc(),
        tz: f.end.tz,
      });
    }
    this.#segments = segments;
    // for no flights, skip anchor/index setup (find() will return ground by flights.length check)
    if (this.flights.length === 0) {
      this.#idxs = [];
      this.#lastPos = 0;
      return;
    }

    const rawIdx: number[] = [0];
    for (let i = 1; i < segments.length; i++) {
      const prev = at(segments, i - 1, "segment");
      const curr = at(segments, i, "segment");
      let gap = 0;
      const prevFlight = getSegmentFlight(prev);
      const currFlight = getSegmentFlight(curr);
      if (prevFlight !== currFlight) {
        const dPrev =
          prev.type === "in-flight"
            ? nonnull(prevFlight, "FlightMapper: in-flight segment").start.ndt.date
            : nonnull(getSegmentDate(prev), "FlightMapper: dated segment");
        const dCurr = nonnull(getSegmentDate(curr), "FlightMapper: dated segment");
        const days = dCurr.differenceInDays(dPrev);
        gap = Math.max(0, days - 1);
      }
      rawIdx[i] = at(rawIdx, i - 1, "raw index") + gap + 1;
    }

    // determine the raw index of the anchor date (could fall on departure, arrival, in-flight, or ground)
    let anchorRaw: Option<number> = null;
    // exact match on departure or arrival day
    // When multiple segments match the anchor date, prefer departure over collapsed over arrival
    const possibleAnchors: number[] = [];
    for (const [i, seg] of segments.entries()) {
      const segDate = getSegmentDate(seg);
      if (segDate?.equals(this.anchor)) {
        possibleAnchors.push(i);
      }
    }
    if (possibleAnchors.length > 0) {
      // Prefer departure > collapsed > arrival segments when multiple match
      const departureIndex = possibleAnchors.find(
        (i) => segments[i]?.type === "departure",
      );
      const collapsedIndex = possibleAnchors.find(
        (i) => segments[i]?.type === "collapsed",
      );
      const arrivalIndex = possibleAnchors.find(
        (i) => segments[i]?.type === "arrival",
      );

      anchorRaw = at(
        rawIdx,
        departureIndex ?? collapsedIndex ?? arrivalIndex ?? at(possibleAnchors, 0, "anchor"),
        "raw index",
      );
    }
    // anchor during in-flight segment
    if (anchorRaw == null) {
      for (const [i, seg] of segments.entries()) {
        if (seg.type === "in-flight") {
          const prevDate = nonnull(getSegmentDate(at(segments, i - 1, "segment")), "FlightMapper: dated segment");
          const nextDate = nonnull(getSegmentDate(at(segments, i + 1, "segment")), "FlightMapper: dated segment");
          if (
            this.anchor.differenceInDays(prevDate) > 0 &&
            nextDate.differenceInDays(this.anchor) > 0
          ) {
            anchorRaw = at(rawIdx, i, "raw index");
            break;
          }
        }
      }
    }
    // anchor on ground day (before first, between, or after last event)
    if (anchorRaw == null) {
      const firstSeg = nonnull(
        segments.find((s) => s.type !== "in-flight"),
        "FlightMapper: a non-in-flight segment",
      );
      const firstDate = nonnull(getSegmentDate(firstSeg), "FlightMapper: dated segment");
      if (this.anchor.differenceInDays(firstDate) < 0) {
        // before first departure
        anchorRaw = at(rawIdx, 0, "raw index") + this.anchor.differenceInDays(firstDate);
      } else {
        // between two non-in-flight events
        let found = false;
        for (let i = 0; i + 1 < segments.length; i++) {
          const curr = at(segments, i, "segment");
          const next = at(segments, i + 1, "segment");
          if (curr.type !== "in-flight" && next.type !== "in-flight") {
            const prevDate = nonnull(getSegmentDate(curr), "FlightMapper: dated segment");
            const nextDate = nonnull(getSegmentDate(next), "FlightMapper: dated segment");
            if (
              this.anchor.differenceInDays(prevDate) > 0 &&
              nextDate.differenceInDays(this.anchor) > 0
            ) {
              anchorRaw = at(rawIdx, i, "raw index") + this.anchor.differenceInDays(prevDate);
              found = true;
              break;
            }
          }
        }
        if (!found) {
          // after last arrival
          const lastPos = segments.length - 1;
          const lastDate = nonnull(getSegmentDate(at(segments, lastPos, "segment")), "FlightMapper: dated segment");
          anchorRaw = at(rawIdx, lastPos, "raw index") + this.anchor.differenceInDays(lastDate);
        }
      }
    }
    const offset = nonnull(anchorRaw, "FlightMapper: anchor index resolved");
    this.#idxs = rawIdx.map((ri) => ri - offset);
    // initialize pointer to the last segment at or before index 0
    let lastPosIndex = 0;
    for (const [i, idx] of this.#idxs.entries()) {
      if (idx <= 0) {
        lastPosIndex = i;
      }
    }
    this.#lastPos = lastPosIndex;
  }

  find(index: number): ResolvedIndex<Info> {
    if (this.flights.length === 0) {
      return {
        type: "ground",
        date: this.anchor.addDays(index),
        tz: this.initialTz,
      };
    }

    const idxs = this.#idxs;
    const idxAt = (p: number): number => at(idxs, p, "index");
    let pos = this.#lastPos;
    if (index >= idxAt(pos)) {
      while (pos + 1 < idxs.length && idxAt(pos + 1) <= index) {
        pos++;
      }
    } else {
      while (pos > 0 && idxAt(pos) > index) {
        pos--;
      }
    }
    this.#lastPos = pos;

    if (idxAt(pos) === index) {
      const seg = at(this.#segments, pos, "segment");
      switch (seg.type) {
        case "departure":
          return {
            type: "departure",
            date: seg.date,
            tp: seg.tpUtc.ndt,
            tz: seg.tz,
          };
        case "arrival":
          return {
            type: "arrival",
            date: seg.date,
            tp: seg.tpUtc.ndt,
            tz: seg.tz,
          };
        case "collapsed":
          return {
            type: "collapsed",
            date: seg.date,
            arrivalTp: seg.arrivalTpUtc.ndt,
            departureTp: seg.departureTpUtc.ndt,
            tz: seg.tz,
            arrivalFlight: seg.arrivalFlight,
            departureFlight: seg.departureFlight,
          };
        case "in-flight":
          return { type: "in-flight", info: seg.flight };
      }
    }

    const firstIdx = idxAt(0);
    const lastIdx = idxAt(idxs.length - 1);
    if (index < firstIdx) {
      const first = at(this.#segments, 0, "segment");
      const firstDate = nonnull(getSegmentDate(first), "FlightMapper: dated segment");
      const firstTz = nonnull(getSegmentTz(first), "FlightMapper: zoned segment");
      const date = firstDate.addDays(index - firstIdx);
      return { type: "ground", date, tz: firstTz };
    }
    if (index > lastIdx) {
      const last = at(this.#segments, this.#segments.length - 1, "segment");
      const lastDate = nonnull(getSegmentDate(last), "FlightMapper: dated segment");
      const lastTz = nonnull(getSegmentTz(last), "FlightMapper: zoned segment");
      const date = lastDate.addDays(index - lastIdx);
      return { type: "ground", date, tz: lastTz };
    }

    const prev = at(this.#segments, pos, "segment");
    const prevFlight = getSegmentFlight(prev);
    const prevDate =
      prev.type === "in-flight"
        ? nonnull(prevFlight, "FlightMapper: in-flight segment").start.ndt.date
        : nonnull(getSegmentDate(prev), "FlightMapper: dated segment");
    const date = prevDate.addDays(index - idxAt(pos));
    const tz =
      prev.type === "in-flight" ? nonnull(prevFlight, "FlightMapper: in-flight segment").start.tz : nonnull(getSegmentTz(prev), "FlightMapper: zoned segment");
    return { type: "ground", date, tz };
  }

  toIndexOpt(date: NaiveDate): Option<number> {
    if (this.flights.length === 0) {
      // all days are ground relative to anchor
      return date.differenceInDays(this.anchor);
    }

    // collect only departure/arrival/collapsed anchors (skip in-flight)
    const anchors: { date: NaiveDate; idx: number }[] = [];
    for (const [pos, seg] of this.#segments.entries()) {
      if (seg.type === "in-flight") continue;
      const segDate = nonnull(getSegmentDate(seg), "FlightMapper: dated segment");
      anchors.push({ date: segDate, idx: at(this.#idxs, pos, "index") });
    }
    anchors.sort((a, b) =>
      a.date.dse === b.date.dse ? a.idx - b.idx : a.date.dse - b.date.dse,
    );

    // exact match on a departure or arrival day (lower idx if two on same day)
    for (const { date: d, idx } of anchors) {
      if (d.equals(date)) {
        return idx;
      }
    }

    const first = at(anchors, 0, "anchor");
    if (date.dse < first.date.dse) {
      // before first departure
      return first.idx + (date.dse - first.date.dse);
    }

    // between anchors
    for (let i = 0; i + 1 < anchors.length; i++) {
      const curr = at(anchors, i, "anchor");
      const next = at(anchors, i + 1, "anchor");
      if (date.dse > curr.date.dse && date.dse < next.date.dse) {
        return curr.idx + (date.dse - curr.date.dse);
      }
    }

    const last = at(anchors, anchors.length - 1, "anchor");
    if (date.dse > last.date.dse) {
      // after last arrival
      return last.idx + (date.dse - last.date.dse);
    }

    return null;
  }

  toIndex(date: NaiveDate, resolution: "upper" | "lower"): number {
    const first = this.toIndexOpt(date);
    if (first == null) {
      // no matching date: return boundaries
      return resolution === "lower"
        ? at(this.#idxs, 0, "index")
        : at(this.#idxs, this.#idxs.length - 1, "index");
    }
    if (resolution === "lower") {
      return first;
    }
    // resolution === "upper": advance through potential in-flight, but only track matching dates
    let last = first;
    let i = first;
    while (true) {
      const next = i + 1;
      const res = this.find(next);
      i = next;
      if (res.type === "in-flight") {
        continue;
      }
      if (res.date.equals(date)) {
        last = next;
        continue;
      }
      break;
    }
    return last;
  }
}
